import React, { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle2,
  MessageSquare,
  Truck
} from "lucide-react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { PageHeader } from "../components/sections/Shared";
import { COMPANY, PACKAGE_TYPES } from "../data/mock";

const FAQS = [
  {
    q: "How fast can you pick up?",
    a: "Pickup times depend on driver availability at the time of your call. Call 518-954-4701 to confirm current response times."
  },
  {
    q: "Are you insured?",
    a: "Insurance and coverage details, including certificates of insurance, are available on request for business accounts. Contact us at 518-954-4701 to discuss requirements for your account."
  },
  {
    q: "Do you run on weekends?",
    a: "24 / 7. We dispatch nights, weekends and major holidays. Holiday surcharges may apply on a small number of dates."
  },
  {
    q: "Can I get a business account?",
    a: "Absolutely — most of our work is recurring B2B routes. You'll get a dedicated dispatcher and monthly invoicing."
  }
];

export default function Contact() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    pickup: "",
    dropoff: "",
    packageType: "",
    serviceLevel: "",
    instructions: ""
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // 1) Save the request to Firestore (source of truth for the Admin dashboard)
      await addDoc(collection(db, "requests"), {
        ...form,
        userId: user?.id || null,
        status: "pending",
        createdAt: serverTimestamp()
      });

      // 2) Fire off an email notification to info@518courier.com via Formsubmit.co
      //    Fire-and-forget — Firestore write is the source of truth.
      //    First submission triggers a verification email at info@518courier.com
      //    (click the link once, then all future emails arrive automatically).
      const fd = new FormData();
      fd.append("_subject", `518 Courier · New quote request from ${form.name || "(no name)"}`);
      fd.append("_replyto", form.email || "");
      fd.append("_template", "table");
      fd.append("_captcha", "false");
      fd.append("Customer name", form.name);
      fd.append("Email", form.email);
      fd.append("Phone", form.phone);
      fd.append("Company", form.company || "—");
      fd.append("Pickup address", form.pickup);
      fd.append("Drop-off address", form.dropoff);
      fd.append("Package type", form.packageType);
      fd.append("Service level", form.serviceLevel);
      fd.append("Instructions", form.instructions || "—");
      fetch("https://formsubmit.co/ajax/info@518courier.com", {
        method: "POST",
        body: fd
      }).catch((err) => console.warn("Formsubmit notification failed:", err?.message));

      toast({
        title: "Request received",
        description: "Dispatch will call you shortly to confirm.",
      });
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        pickup: "",
        dropoff: "",
        packageType: "",
        serviceLevel: "",
        instructions: ""
      });
    } catch (err) {
      toast({
        title: "Couldn't send request",
        description: err?.message || "Please try again or call dispatch.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Request a Courier Quote in Albany, NY | 518 Courier"
        description="Request a same-day, medical, business or interstate courier quote from 518 Courier. Fast dispatch across Albany and the Capital Region. Call 518-954-4701."
        path="/get-a-quote"
      />
      <PageHeader
        eyebrow="Get a quote"
        title="Tell us what you need to"
        accent="move."
        description="Fill out the form below and dispatch will call you to confirm pricing, ETA and pickup details. Need it right now? Call us — we always pick up."
        image="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMGRyaXZlcnxlbnwwfHx8fDE3ODAyNTM0MDh8MA&ixlib=rb-4.1.0&q=85"
      />

      {/* CONTACT GRID */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container-518 grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-9">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847]">
                    Thanks — we got it.
                  </h3>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">
                    Dispatch will call you shortly to confirm pricing
                    and assign a driver. If it's urgent, call{" "}
                    <a
                      className="text-[#f59321] font-bold"
                      href={`tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`}
                    >
                      {COMPANY.phone}
                    </a>{" "}
                    now.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="mt-7 bg-[#0f2847] hover:bg-[#1e3a5f] text-white rounded-full h-12 px-6 font-semibold"
                  >
                    Submit another request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847]">
                      Request a delivery
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                      All fields with * are required.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-[#0f2847] font-semibold">
                        Full name *
                      </Label>
                      <Input
                        id="name"
                        required
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Jane Doe"
                        className="h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="company" className="text-[#0f2847] font-semibold">
                        Company
                      </Label>
                      <Input
                        id="company"
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        placeholder="Acme Co."
                        className="h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-[#0f2847] font-semibold">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="jane@acme.com"
                        className="h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-[#0f2847] font-semibold">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="(518) 555-0123"
                        className="h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="pickup" className="text-[#0f2847] font-semibold">
                      Pickup address *
                    </Label>
                    <Input
                      id="pickup"
                      required
                      value={form.pickup}
                      onChange={(e) => update("pickup", e.target.value)}
                      placeholder="123 Main St, Albany, NY 12207"
                      className="h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="dropoff" className="text-[#0f2847] font-semibold">
                      Drop-off address *
                    </Label>
                    <Input
                      id="dropoff"
                      required
                      value={form.dropoff}
                      onChange={(e) => update("dropoff", e.target.value)}
                      placeholder="456 State St, Schenectady, NY 12305"
                      className="h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-[#0f2847] font-semibold">Package type *</Label>
                      <Select
                        value={form.packageType}
                        onValueChange={(v) => update("packageType", v)}
                      >
                        <SelectTrigger className="h-12 rounded-xl border-slate-200 focus:ring-[#f59321]">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PACKAGE_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[#0f2847] font-semibold">Service level *</Label>
                      <Select
                        value={form.serviceLevel}
                        onValueChange={(v) => update("serviceLevel", v)}
                      >
                        <SelectTrigger className="h-12 rounded-xl border-slate-200 focus:ring-[#f59321]">
                          <SelectValue placeholder="How fast?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard (same-day)</SelectItem>
                          <SelectItem value="rush">Rush (fastest available)</SelectItem>
                          <SelectItem value="scheduled">Scheduled / recurring</SelectItem>
                          <SelectItem value="overnight">Overnight</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="instructions" className="text-[#0f2847] font-semibold">
                      Special instructions
                    </Label>
                    <Textarea
                      id="instructions"
                      rows={4}
                      value={form.instructions}
                      onChange={(e) => update("instructions", e.target.value)}
                      placeholder="Loading dock on 2nd Ave, ring buzzer #4, fragile contents…"
                      className="rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321] resize-none"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-13 px-8 h-14 font-bold text-base shadow-md disabled:opacity-60"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {submitting ? "Sending…" : "Send request"}
                    </Button>
                    <a
                      href={`tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`}
                      className="sm:ml-auto"
                    >
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full sm:w-auto bg-white border-2 border-[#0f2847] text-[#0f2847] hover:bg-[#0f2847] hover:text-white rounded-full h-14 px-7 font-semibold"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Or call dispatch
                      </Button>
                    </a>
                  </div>
                  <p className="text-xs text-slate-500 pt-2">
                    By submitting, you agree to be contacted about your delivery
                    request. We do not share your information.
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* Side info */}
          <aside className="lg:col-span-4 space-y-5">
            <div className="bg-[#0f2847] text-white rounded-2xl p-7 stripe-bg">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
                Right now
              </div>
              <h3 className="font-display text-2xl font-extrabold mb-2">
                Dispatch is live.
              </h3>
              <p className="text-sm text-slate-300 mb-5 leading-relaxed">
                Real humans answer the phone 24 hours a day, every day of the
                year.
              </p>
              <a
                href={`tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`}
                className="flex items-center gap-3 text-white font-display font-extrabold text-2xl hover:text-[#f59321] transition-colors"
              >
                <Phone className="w-6 h-6" />
                {COMPANY.phone}
              </a>
            </div>

            <ContactCard icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
            <ContactCard icon={MapPin} label="Office" value={COMPANY.address} />
            <ContactCard icon={Clock} label="Hours" value={COMPANY.hours} />
            <ContactCard
              icon={MessageSquare}
              label="Text dispatch"
              value="Reply STOP to opt out"
              href={`sms:${COMPANY.phone.replace(/[^0-9]/g, "")}`}
            />
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-518 max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-3">
              <span className="w-6 h-[2px] bg-[#f59321]" />
              Frequently asked
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0f2847] leading-[1.1]">
              Quick answers.
            </h2>
          </div>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="group py-5 cursor-pointer"
              >
                <summary className="flex items-center justify-between gap-4 list-none">
                  <h4 className="font-display font-bold text-[#0f2847] text-lg">
                    {f.q}
                  </h4>
                  <span className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-[#0f2847] group-open:bg-[#f59321] group-open:text-white group-open:border-[#f59321] transition-colors shrink-0">
                    <Truck className="w-4 h-4" />
                  </span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ContactCard({ icon: Icon, label, value, href }) {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap
      href={href}
      className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#f59321] transition-colors group"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#f59321]/10 text-[#f59321] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-0.5">
            {label}
          </div>
          <div className="text-[#0f2847] font-semibold group-hover:text-[#f59321] transition-colors break-words">
            {value}
          </div>
        </div>
      </div>
    </Wrap>
  );
}
